'use client';

import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Edit2, Trash2, Eye, AlertTriangle, CheckSquare, Square, Loader2,  } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import StatusBadge from './StatusBadge';
import PriorityBadge from './PriorityBadge';
import TaskDeleteModal from './TaskDeleteModal';

// Backend integration point: replace with API call to fetch tasks for current sprint
export const allTasks = [
  { id: 'task-001', name: 'Redesign Module 3 learning objectives', assignee: 'Priya Nair', assigneeId: 'priya-nair', project: 'Curriculum Q4', priority: 'high' as const, status: 'in-progress' as const, dueDate: 'Aug 28', daysOverdue: 3 },
  { id: 'task-002', name: 'Review assessment rubric for Unit 7', assignee: 'Sofía Delgado', assigneeId: 'sofia-delgado', project: 'Assessment Revamp', priority: 'high' as const, status: 'in-review' as const, dueDate: 'Aug 29', daysOverdue: 2 },
  { id: 'task-003', name: 'Upload revised lesson plans to LMS', assignee: 'Tariq Hassan', assigneeId: 'tariq-hassan', project: 'LMS Migration', priority: 'high' as const, status: 'blocked' as const, dueDate: 'Aug 26', daysOverdue: 5 },
  { id: 'task-004', name: 'Draft storyboard for video lecture series', assignee: 'Kwame Asante', assigneeId: 'kwame-asante', project: 'Video Content', priority: 'medium' as const, status: 'in-progress' as const, dueDate: 'Sep 2', daysOverdue: 0 },
  { id: 'task-005', name: 'Peer review: Advanced Biology Unit 2', assignee: 'Lena Voigt', assigneeId: 'lena-voigt', project: 'Curriculum Q4', priority: 'medium' as const, status: 'in-review' as const, dueDate: 'Sep 1', daysOverdue: 0 },
  { id: 'task-006', name: 'Write quiz questions for Chapter 5', assignee: 'Raj Mehta', assigneeId: 'raj-mehta', project: 'Assessment Revamp', priority: 'low' as const, status: 'backlog' as const, dueDate: 'Sep 5', daysOverdue: 0 },
  { id: 'task-007', name: 'Audit accessibility of online modules', assignee: 'Chioma Eze', assigneeId: 'chioma-eze', project: 'LMS Migration', priority: 'medium' as const, status: 'in-progress' as const, dueDate: 'Sep 3', daysOverdue: 0 },
  { id: 'task-008', name: 'Finalize teacher guide for Semester 2', assignee: 'Priya Nair', assigneeId: 'priya-nair', project: 'Curriculum Q4', priority: 'high' as const, status: 'blocked' as const, dueDate: 'Aug 27', daysOverdue: 4 },
  { id: 'task-009', name: 'Coordinate with external SME for review', assignee: 'Tariq Hassan', assigneeId: 'tariq-hassan', project: 'Video Content', priority: 'medium' as const, status: 'in-progress' as const, dueDate: 'Aug 30', daysOverdue: 1 },
  { id: 'task-010', name: 'Translate Module 1 content to Spanish', assignee: 'Sofía Delgado', assigneeId: 'sofia-delgado', project: 'Localization', priority: 'low' as const, status: 'backlog' as const, dueDate: 'Sep 8', daysOverdue: 0 },
  { id: 'task-011', name: 'Record narration for Unit 4 slides', assignee: 'Amara Osei', assigneeId: 'amara-osei', project: 'Video Content', priority: 'low' as const, status: 'done' as const, dueDate: 'Aug 25', daysOverdue: 0 },
  { id: 'task-012', name: 'Update competency framework documentation', assignee: 'Lena Voigt', assigneeId: 'lena-voigt', project: 'Curriculum Q4', priority: 'medium' as const, status: 'done' as const, dueDate: 'Aug 24', daysOverdue: 0 },
  { id: 'task-013', name: 'QA test new quiz engine integration', assignee: 'Kwame Asante', assigneeId: 'kwame-asante', project: 'LMS Migration', priority: 'high' as const, status: 'in-review' as const, dueDate: 'Aug 31', daysOverdue: 0 },
  { id: 'task-014', name: 'Draft onboarding checklist for new educators', assignee: 'Raj Mehta', assigneeId: 'raj-mehta', project: 'Curriculum Q4', priority: 'low' as const, status: 'in-progress' as const, dueDate: 'Sep 4', daysOverdue: 0 },
  { id: 'task-015', name: 'Review and approve Module 5 content draft', assignee: 'Chioma Eze', assigneeId: 'chioma-eze', project: 'Assessment Revamp', priority: 'medium' as const, status: 'backlog' as const, dueDate: 'Sep 6', daysOverdue: 0 },
];

type SortKey = 'name' | 'assignee' | 'project' | 'priority' | 'status' | 'dueDate' | 'daysOverdue';
type SortDir = 'asc' | 'desc' | null;

const ITEMS_PER_PAGE_OPTIONS = [
  { id: 'ipp-5', value: 5, label: '5 per page' },
  { id: 'ipp-10', value: 10, label: '10 per page' },
  { id: 'ipp-15', value: 15, label: '15 per page' },
];

const statusDropdownOptions = [
  { id: 'sd-backlog', value: 'backlog', label: 'Backlog' },
  { id: 'sd-inprogress', value: 'in-progress', label: 'In Progress' },
  { id: 'sd-review', value: 'in-review', label: 'In Review' },
  { id: 'sd-done', value: 'done', label: 'Done' },
  { id: 'sd-blocked', value: 'blocked', label: 'Blocked' },
];

export default function TaskTable() {
  const [tasks, setTasks] = useState(allTasks);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [statusDropdown, setStatusDropdown] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'));
      if (sortDir === 'desc') setSortKey(null);
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const sortedTasks = useMemo(() => {
    if (!sortKey || !sortDir) return tasks;
    return [...tasks].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [tasks, sortKey, sortDir]);

  const totalPages = Math.ceil(sortedTasks.length / itemsPerPage);
  const paginatedTasks = sortedTasks.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const allSelected =
    paginatedTasks.length > 0 &&
    paginatedTasks.every((t) => selectedIds.has(t.id));
  const someSelected = paginatedTasks.some((t) => selectedIds.has(t.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      const next = new Set(selectedIds);
      paginatedTasks.forEach((t) => next.delete(t.id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      paginatedTasks.forEach((t) => next.add(t.id));
      setSelectedIds(next);
    }
  };

  const toggleSelectRow = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleStatusChange = (taskId: string, newStatus: typeof statusDropdownOptions[0]['value']) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus as typeof t.status } : t))
    );
    setStatusDropdown(null);
    toast.success('Task status updated');
  };

  const handleDeleteRow = (taskId: string) => {
    setRemovingIds((prev) => new Set([...prev, taskId]));
    setTimeout(() => {
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      setRemovingIds((prev) => {
        const n = new Set(prev);
        n.delete(taskId);
        return n;
      });
      setDeleteTarget(null);
      toast.success('Task removed');
    }, 220);
  };

  const handleBulkDelete = () => {
    setBulkDeleting(true);
    setTimeout(() => {
      setTasks((prev) => prev.filter((t) => !selectedIds.has(t.id)));
      const count = selectedIds.size;
      setSelectedIds(new Set());
      setBulkDeleting(false);
      toast.success(`${count} task${count > 1 ? 's' : ''} deleted`);
    }, 400);
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={13} className="opacity-40" />;
    if (sortDir === 'asc') return <ChevronUp size={13} className="text-primary" />;
    return <ChevronDown size={13} className="text-primary" />;
  };

  const colHeader = (label: string, key: SortKey) => (
    <th
      onClick={() => handleSort(key)}
      className="px-4 py-3 text-left text-[12px] font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors whitespace-nowrap"
    >
      <span className="flex items-center gap-1.5">
        {label}
        <SortIcon col={key} />
      </span>
    </th>
  );

  const pageNumbers: number[] = [];
  for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);

  return (
    <div className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
      <Toaster position="bottom-right" richColors />

      {/* Table header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
        <div>
          <h2 className="text-base font-semibold text-foreground">Sprint Tasks</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {sortedTasks.length} tasks · Sprint W35
          </p>
        </div>
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div className="flex items-center gap-3 px-5 py-2.5 bg-primary/5 border-b border-primary/20 slide-up">
          <span className="text-sm font-semibold text-primary">
            {selectedIds.size} selected
          </span>
          <div className="flex-1" />
          <button
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm font-medium hover:bg-red-100 transition-colors btn-press disabled:opacity-60"
          >
            {bulkDeleting ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Trash2 size={13} />
            )}
            Delete selected
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-muted-foreground hover:text-foreground font-medium btn-press"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[900px]">
          <thead className="bg-muted/40">
            <tr>
              <th className="px-4 py-3 w-10">
                <button onClick={toggleSelectAll} className="text-muted-foreground hover:text-primary transition-colors">
                  {allSelected ? (
                    <CheckSquare size={16} className="text-primary" />
                  ) : (
                    <Square size={16} />
                  )}
                </button>
              </th>
              {colHeader('Task Name', 'name')}
              {colHeader('Assignee', 'assignee')}
              {colHeader('Project', 'project')}
              {colHeader('Priority', 'priority')}
              {colHeader('Status', 'status')}
              {colHeader('Due Date', 'dueDate')}
              {colHeader('Overdue', 'daysOverdue')}
              <th className="px-4 py-3 w-16" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedTasks.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-5 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <CheckSquare size={36} className="text-muted-foreground/40" />
                    <p className="text-base font-semibold text-foreground">No tasks match your filters</p>
                    <p className="text-sm text-muted-foreground">
                      Try adjusting the status, priority, or assignee filters above.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedTasks.map((task) => {
                const isRemoving = removingIds.has(task.id);
                const isSelected = selectedIds.has(task.id);
                const isHovered = hoveredRow === task.id;
                const isStatusOpen = statusDropdown === task.id;

                return (
                  <tr
                    key={task.id}
                    onMouseEnter={() => setHoveredRow(task.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    className={`transition-all duration-200 ${
                      isRemoving ? 'opacity-0 max-h-0' : 'opacity-100'
                    } ${isSelected ? 'bg-primary/5' : isHovered ? 'bg-muted/50' : ''}`}
                  >
                    {/* Checkbox */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleSelectRow(task.id)}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        {isSelected ? (
                          <CheckSquare size={15} className="text-primary" />
                        ) : (
                          <Square size={15} />
                        )}
                      </button>
                    </td>

                    {/* Task name */}
                    <td className="px-4 py-3 max-w-[220px]">
                      <div className="flex items-center gap-2">
                        {task.daysOverdue > 0 && (
                          <AlertTriangle size={13} className="text-red-500 shrink-0" />
                        )}
                        <span className="text-sm font-medium text-foreground truncate" title={task.name}>
                          {task.name}
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">{task.id}</p>
                    </td>

                    {/* Assignee */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-foreground">{task.assignee}</span>
                    </td>

                    {/* Project */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm text-muted-foreground">{task.project}</span>
                    </td>

                    {/* Priority */}
                    <td className="px-4 py-3">
                      <PriorityBadge priority={task.priority} />
                    </td>

                    {/* Status — inline dropdown */}
                    <td className="px-4 py-3 relative">
                      <button
                        onClick={() => setStatusDropdown(isStatusOpen ? null : task.id)}
                        className="btn-press"
                        title="Click to change status"
                      >
                        <StatusBadge status={task.status} />
                      </button>
                      {isStatusOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setStatusDropdown(null)}
                          />
                          <div className="absolute left-0 top-full mt-1 z-20 bg-card border border-border rounded-xl shadow-card-md min-w-[160px] py-1 fade-in">
                            {statusDropdownOptions.map((opt) => (
                              <button
                                key={opt.id}
                                onClick={() => handleStatusChange(task.id, opt.value)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors"
                              >
                                <StatusBadge status={opt.value as typeof task.status} />
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </td>

                    {/* Due date */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`text-sm font-medium font-tabular ${
                          task.daysOverdue > 0 ? 'text-red-600' : 'text-foreground'
                        }`}
                      >
                        {task.dueDate}
                      </span>
                    </td>

                    {/* Days overdue */}
                    <td className="px-4 py-3">
                      {task.daysOverdue > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full font-tabular">
                          +{task.daysOverdue}d
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>

                    {/* Row actions */}
                    <td className="px-4 py-3">
                      <div
                        className={`flex items-center gap-0.5 transition-opacity duration-150 ${
                          isHovered || isSelected ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <button
                          title="View task details"
                          className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors btn-press"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          title="Edit task"
                          className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors btn-press"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          title="Delete task — this cannot be undone"
                          onClick={() => setDeleteTarget(task.id)}
                          className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors btn-press"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 border-t border-border bg-muted/20">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Show</span>
          <div className="relative">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="h-8 pl-2.5 pr-7 rounded-lg border border-border bg-card text-sm text-foreground font-medium appearance-none cursor-pointer hover:border-primary/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {ITEMS_PER_PAGE_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
          <span className="text-xs text-muted-foreground">
            · {sortedTasks.length} total tasks
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="h-8 px-3 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors btn-press"
          >
            Previous
          </button>
          {pageNumbers.map((num) => (
            <button
              key={`page-${num}`}
              onClick={() => setCurrentPage(num)}
              className={`h-8 w-8 rounded-lg text-sm font-medium transition-colors btn-press ${
                currentPage === num
                  ? 'bg-primary text-primary-foreground border border-primary'
                  : 'border border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
              }`}
            >
              {num}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="h-8 px-3 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors btn-press"
          >
            Next
          </button>
        </div>
      </div>

      {/* Delete confirm modal */}
      {deleteTarget && (
        <TaskDeleteModal
          taskId={deleteTarget}
          taskName={tasks.find((t) => t.id === deleteTarget)?.name ?? ''}
          onConfirm={() => handleDeleteRow(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}